import {
    U2bColumnDefinitionExternalComponent,
    U2bColumnPipeName,
    U2bFilterSelectOptionsRelations,
    U2bTableData
} from '@core/components/layout/table/table.types';
import { BcmUserTableSettingKey } from '@shared/models/bcm-settings-user';
import { getIsActive } from '@core/functions/get-is-active';
import { paymentTypeTranslationKeys, Person } from '@shared/models/person';
import { FilterFieldType } from '@core/datafilter/available-filter-fields';
import { FILTER_OPERATORS_DEFAULT_SELECT, FILTER_OPERATORS_DEFAULT_STRING_ALT } from '@core/datafilter/constants';
import { DataFilterOperator } from '@core/datafilter/filter-operator';
import { BcmTenantType } from '@modules/bcm/@shared/enums/bcm-tenant-type';

export const personsTableData: U2bTableData = {
    tableId: BcmUserTableSettingKey.Persons,
    hasServerSideFiltering: true,
    columnDefinitions: [
        {
            property: 'avatar',
            name: 'Avatar',
            translationKey: 'person.table.avatar',
            hideNameInHeader: true,
            isActive: getIsActive(),
            isAvatar: true,
            avatarAttribute: 'image'
        },
        {
            property: 'identNumber',
            name: 'Ident.-Nr.',
            translationKey: 'person.table.identNumber',
            isActive: getIsActive(),
            filter: {}
        },
        {
            property: 'up2boatId',
            name: 'Up2Boat-ID',
            translationKey: 'person.table.up2boatId',
            isActive: getIsActive(false, false, false),
            filter: {}
        },
        {
            property: 'lastName',
            name: 'Nachname',
            translationKey: 'person.table.lastName',
            isActive: getIsActive(),
            filter: {}
        },
        {
            property: 'firstName',
            name: 'Vorname',
            translationKey: 'person.table.firstName',
            isActive: getIsActive(),
            filter: {}
        },
        {
            property: 'tenantRelationId',
            name: 'Beziehung',
            translationKey: 'person.table.tenantRelation',
            isActive: getIsActive(),
            exportKey: 'tenantRelationId',
            externalComponent: U2bColumnDefinitionExternalComponent.TenantRelationColoredName,
            parseItem: (item: Person) => item.activeAndNextTenantRelationAssignments,
            filter: {
                property: 'tenantRelationId',
                fieldType: FilterFieldType.AutoComplete,
                compareAttribute: 'id',
                filterMultiple: true,
                operators: [
                    DataFilterOperator.Contains,
                    DataFilterOperator.NotContains,
                    DataFilterOperator.IsEmpty
                ],
                selectOptionsRelation: U2bFilterSelectOptionsRelations.TenantRelations,
                selectOptionsFilterBy: ['name'],
                selectOptionsDisplayWith: 'name',
            }
        },
        {
            property: 'fullAddress',
            name: 'Adresse',
            translationKey: 'person.table.address',
            isActive: getIsActive(),
            exportKey: 'fullAddress',
            filter: {}
        },
        {
            property: 'mail',
            name: 'Email',
            translationKey: 'person.table.email',
            isActive: getIsActive(),
            exportKey: 'mail',
            filter: {}
        },
        {
            property: 'phone',
            name: 'Tel.',
            translationKey: 'person.table.phone',
            isActive: getIsActive(),
            exportKey: 'phone',
            filter: {}
        },
        {
            property: 'IBAN',
            name: 'IBAN',
            translationKey: 'person.table.iban',
            isActive: getIsActive(false, false, false),
            filter: {}
        },
        {
            property: 'BIC',
            name: 'BIC',
            translationKey: 'person.table.bic',
            isActive: getIsActive(false, false, false),
            filter: {}
        },
        {
            property: 'city',
            name: 'Wohnort',
            translationKey: 'person.table.city',
            isActive: getIsActive(false, false, false),
            filter: {}
        },
        {
            property: 'country',
            name: 'Land',
            translationKey: 'person.table.country',
            isActive: getIsActive(false, false, false),
            parseItem: (item: Person) => item.country?.de,
            filter: {
                property: 'country',
                fieldName: 'de',
                fieldType: FilterFieldType.AutoComplete,
                operators: FILTER_OPERATORS_DEFAULT_SELECT,
                selectOptionsRelation: U2bFilterSelectOptionsRelations.Countries,
                selectOptionsFilterBy: ['de'],
                selectOptionsDisplayWith: 'de'
            }
        },
        {
            property: 'birthDate',
            name: 'Geburtsdatum',
            translationKey: 'person.table.birthDate',
            isActive: getIsActive(false, false, false),
            pipe: U2bColumnPipeName.Date,
            filter: {
                fieldType: FilterFieldType.Date
            }
        },
        {
            property: 'note',
            name: 'Notizen',
            translationKey: 'person.table.notes',
            isActive: getIsActive(false, false, false),
            filter: {
                operators: FILTER_OPERATORS_DEFAULT_STRING_ALT
            }
        },
        {
            property: 'taxNumber',
            name: 'Steuer-Nr.',
            translationKey: 'person.table.taxNumber',
            isActive: getIsActive(false, false, false),
            excludeIf: tenant => tenant.productType !== BcmTenantType.InlandPort,
            filter: {},
        },
        {
            property: 'Kreditinstitut',
            name: 'Kreditinstitut',
            translationKey: 'person.table.bankName',
            isActive: getIsActive(false, false, false),
            filter: {},
        },
        {
            property: 'accountHolder',
            name: 'Kontoinhaber',
            translationKey: 'person.table.accountHolder',
            isActive: getIsActive(false, false, false),
            filter: {},
        },
        {
            property: 'SEPAMandatId',
            name: 'SEPA Mandat ID',
            translationKey: 'person.table.sepaMandatId',
            isActive: getIsActive(false, false, false),
            filter: {},
        },
        {
            property: 'SEPAMandatSignDate',
            name: 'SEPA Datum',
            translationKey: 'person.table.sepaDate',
            isActive: getIsActive(false, false, false),
            pipe: U2bColumnPipeName.Date,
            filter: {
                fieldType: FilterFieldType.Date
            }
        },
        {
            property: 'entryDate',
            name: 'Eintritt',
            translationKey: 'person.table.entryDate',
            isActive: getIsActive(false, false, false),
            pipe: U2bColumnPipeName.Date,
            filter: {
                fieldType: FilterFieldType.Date
            }
        },
        {
            property: 'exitDate',
            name: 'Austritt',
            translationKey: 'person.table.exitDate',
            isActive: getIsActive(false, false, false),
            pipe: U2bColumnPipeName.Date,
            filter: {
                fieldType: FilterFieldType.Date
            }
        },
        {
            property: 'invoicesByMail',
            name: 'Belege per Mail',
            translationKey: 'person.table.invoicesByMail',
            isActive: getIsActive(false, false, false),
            parseItem: (person: Person) => person.invoicesByMail ?  '✔' : '',
            filter: {},
        },
        {
            property: 'invoicesByPost',
            name: 'Belege per Post',
            translationKey: 'person.table.invoicesByPost',
            isActive: getIsActive(false, false, false),
            parseItem: (person: Person) => person.invoicesByPost ?  '✔' : '',
            filter: {},
        },
        {
            property: 'job',
            name: 'Beruf',
            translationKey: 'person.table.job',
            isActive: getIsActive(false, false, false),
            filter: {}
        },
        {
            property: 'companyContacts',
            name: 'Organisation',
            translationKey: 'person.table.organization',
            isActive: getIsActive(false, false, false),
            parseItem: (person: Person) => person?.companyContacts.length ?
                person?.companyContacts.map(company => company?.company.name) : '',
            filter: {
                property: 'companyContacts',
                fieldType: FilterFieldType.Text,
                compareAttribute: 'company?.company.name',
                operators: [
                    DataFilterOperator.Contains,
                    DataFilterOperator.NotContains,
                    DataFilterOperator.IsEmpty
                ],
                selectOptionsDisplayWith: 'company?.company.name',
            }
        },
        {
            property: 'salutationMail',
            name: 'Anrede E-Mail',
            translationKey: 'person.table.emailSalutation',
            isActive: getIsActive(false, false, false),
            filter: {}
        },
        {
            property: 'invoicePaymentType',
            name: 'Rechnung Standard Zahlart',
            translationKey: 'person.table.defaultPaymentType',
            isActive: getIsActive(false, false, false),
            parseItem: (item: Person) => paymentTypeTranslationKeys[item.invoicePaymentType] ?? item.invoicePaymentType,
            filter: {}
        },
    ]
};
